import { useTheme } from '@mui/material/styles'
import * as React from 'react'
import { useCallback } from 'react'
import { MatchPairProps } from '../typings'
import {
  LocationMatch,
  getExternalLocationAddress as formatLabel,
} from '@user/store/entityLocations'
import { matchServiceCreator } from '../../hooks'

import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import { useGetTaxRatesOnMount } from '@user/store/serviceTax'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'

type Props = Pick<MatchPairProps, 'state' | 'dispatch' | 'location'> & {
  match?: LocationMatch
}

export const LocationSelector: React.FC<Props> = (props) => {
  const {
    location: { id },
    state: {
      externalLocations: serviceLocations,
      externalBillTo: billToLocation,
      matches,
    },
    dispatch,
    match,
  } = props

  const disabled = !billToLocation
  const { taxRateStatus, taxRates } = useGetTaxRatesOnMount()
  const loading = taxRateStatus === 'pending'
  const value = match?.createNewLocation
    ? 'create'
    : match?.externalEntityId ?? -1

  const locationsInUse = React.useMemo(
    () =>
      [...matches.values()]
        .filter((x) => x.locationId !== id)
        .reduce<Record<string, boolean>>(
          (acc, m) =>
            m.createNewLocation ? acc : { ...acc, [m.externalEntityId]: true },
          {},
        ),
    [id, matches],
  )

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = event.target

      const payload: LocationMatch =
        value === 'create'
          ? {
              locationId: id,
              externalEntityType: 'Location',
              createNewLocation: true,
            }
          : {
              locationId: id,
              externalEntityId: value,
              externalEntityType: 'Location',
              createNewLocation: false,
              taxCodeId: taxRates?.find(
                (e) =>
                  e.name ===
                  serviceLocations.find((e) => e.id === value)?.taxCode,
              )?.id,
            }

      dispatch(matchServiceCreator(payload))
    },
    [dispatch, id, taxRates, serviceLocations],
  )
  const theme = useTheme()
  return (
    <>
      <Typography>Match to Service Location</Typography>
      {loading && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexGrow={1}
        >
          <CircularProgress size="30px" />
        </Box>
      )}
      {taxRateStatus === 'success' && (
        <TextField
          disabled={disabled}
          value={value}
          onChange={onChange}
          size="small"
          fullWidth
          select
          style={{ margin: `${theme.spacing(1.6)} 0` }}
          data-test-id="locationSelector"
        >
          <MenuItem value={-1} disabled>
            Select Service Location
          </MenuItem>
          <MenuItem value="create">No Match - Create New Location</MenuItem>
          {serviceLocations.map((l) => (
            <MenuItem key={l.id} value={l.id} disabled={locationsInUse[l.id]}>
              {formatLabel(l)}
            </MenuItem>
          ))}
        </TextField>
      )}
    </>
  )
}
LocationSelector.displayName = 'LocationSelector'
